import React from 'react';
import { FileStack, Gavel, Scale, ShieldCheck } from 'lucide-react';
import PartiesSection from './sections/PartiesSection';
import HearingsSection from './sections/HearingsSection';
import MemorandumsSection from './sections/MemorandumsSection';
import RequestsSection from './sections/RequestsSection';
import ReportsSection from './sections/ReportsSection';
import SessionRecordsSection from './sections/SessionRecordsSection';
import AuthorityDecisionsSection from './sections/AuthorityDecisionsSection';
import AttachmentsSection from './sections/AttachmentsSection';
import TicketsSection from './sections/TicketsSection';
import LawyerSection from './sections/LawyerSection';
import AdditionalFeesSection from './sections/AdditionalFeesSection';
import VideoMeetingSection from './sections/VideoMeetingSection';

const PALETTE = {
  navy: '#0F2742',
  green: '#1F8A70',
  gold: '#C8A45C',
  border: '#DCE7E3',
  text: '#475569',
};

const hasMojibake = (value = '') => /[ØÙ\uFFFD]/.test(value);

const safeValue = (value, fallback) => {
  if (!value || hasMojibake(value)) {
    return fallback;
  }

  return value;
};

const OVERVIEW_SECTIONS = [
  {
    id: 'subject',
    title: 'موضوع القضية',
    icon: FileStack,
    color: PALETTE.green,
    fallback: 'ملف قانوني محل متابعة لدى وازن.',
  },
  {
    id: 'basis',
    title: 'السند القانوني',
    icon: Scale,
    color: PALETTE.gold,
    fallback: 'الاستناد إلى الأنظمة ذات الصلة وما يدعم مصلحة العميل في الملف.',
  },
  {
    id: 'requests',
    title: 'طلبات العميل',
    icon: ShieldCheck,
    color: PALETTE.navy,
    fallback: 'الطلبات النظامية المطلوب السير عليها بما يحفظ المصلحة القانونية.',
  },
  {
    id: 'journey',
    title: 'المسار القانوني لوازن',
    icon: Gavel,
    color: PALETTE.green,
    fallback: 'تحديد الإجراء القانوني الأنسب ومتابعته خطوة بخطوة حتى اكتمال الملف.',
  },
];

const readClientRequests = (caseData) => {
  if (Array.isArray(caseData?.requests) && caseData.requests.length) {
    return caseData.requests
      .map((item) => safeValue(item?.title || item?.request_type, ''))
      .filter(Boolean)
      .join(' — ');
  }

  return OVERVIEW_SECTIONS[2].fallback;
};

const readJourney = (caseData) => {
  if (Array.isArray(caseData?.sessionRecords) && caseData.sessionRecords.length) {
    return caseData.sessionRecords
      .map((item) => safeValue(item?.title || item?.description, ''))
      .filter(Boolean)
      .slice(0, 2)
      .join(' — ');
  }

  if (Array.isArray(caseData?.reports) && caseData.reports.length) {
    return caseData.reports
      .map((item) => safeValue(item?.summary || item?.title, ''))
      .filter(Boolean)
      .slice(0, 2)
      .join(' — ');
  }

  return OVERVIEW_SECTIONS[3].fallback;
};

const OverviewCard = ({ title, icon: Icon, color, value }) => (
  <article
    style={{
      background: '#FFFFFF',
      border: `1px solid ${PALETTE.border}`,
      borderRadius: 24,
      padding: '22px 22px 20px',
      boxShadow: '0 16px 34px rgba(15, 39, 66, 0.05)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <span
      style={{
        position: 'absolute',
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        width: 5,
        background: color,
      }}
    />

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 14,
      }}
    >
      <span
        style={{
          width: 42,
          height: 42,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 14,
          background: `${color}18`,
          color,
          flexShrink: 0,
        }}
      >
        <Icon size={20} />
      </span>
      <h3
        style={{
          margin: 0,
          color: PALETTE.navy,
          fontSize: 18,
          fontWeight: 900,
        }}
      >
        {title}
      </h3>
    </div>

    <p
      style={{
        margin: 0,
        color: PALETTE.text,
        lineHeight: 2,
        fontSize: 14,
      }}
    >
      {value}
    </p>
  </article>
);

const CaseMainPanel = ({ activeSection, caseData }) => {
  const overviewValues = {
    subject: safeValue(caseData?.subject, OVERVIEW_SECTIONS[0].fallback),
    basis: safeValue(caseData?.legal_basis, OVERVIEW_SECTIONS[1].fallback),
    requests: readClientRequests(caseData),
    journey: readJourney(caseData),
  };

  switch (activeSection) {
    case 'parties':
      return <PartiesSection caseData={caseData} />;
    case 'hearings':
      return <HearingsSection caseData={caseData} />;
    case 'memorandums':
      return <MemorandumsSection caseData={caseData} />;
    case 'requests':
      return <RequestsSection caseData={caseData} />;
    case 'reports':
      return <ReportsSection caseData={caseData} />;
    case 'sessionRecords':
      return <SessionRecordsSection caseData={caseData} />;
    case 'authorityDecisions':
      return <AuthorityDecisionsSection caseData={caseData} />;
    case 'attachments':
      return <AttachmentsSection caseData={caseData} />;
    case 'tickets':
      return <TicketsSection caseData={caseData} />;
    case 'lawyer':
      return <LawyerSection caseData={caseData} />;
    case 'fees':
      return <AdditionalFeesSection caseData={caseData} />;
    case 'videoMeeting':
      return <VideoMeetingSection caseData={caseData} />;
    case 'overview':
    default:
      return (
        <section
          style={{
            display: 'grid',
            gap: 16,
          }}
        >
          {OVERVIEW_SECTIONS.map((section) => (
            <OverviewCard
              key={section.id}
              title={section.title}
              icon={section.icon}
              color={section.color}
              value={overviewValues[section.id]}
            />
          ))}
        </section>
      );
  }
};

export default CaseMainPanel;
